import DOMPurify, { type Config } from 'dompurify'

const SAFE_CSS_PROPERTIES = new Set([
  'text-align', 'font-size', 'font-weight', 'font-style', 'font-family',
  'color', 'background-color',
  'padding', 'padding-left', 'padding-right', 'padding-top', 'padding-bottom',
  'margin', 'margin-left', 'margin-right', 'margin-top', 'margin-bottom',
  'border', 'border-collapse', 'border-spacing',
  'width', 'height', 'min-width', 'max-width', 'min-height', 'max-height',
  'vertical-align', 'white-space', 'line-height',
  'text-decoration', 'text-indent', 'letter-spacing', 'word-spacing',
  'list-style-type', 'display', 'overflow'
])

const DANGEROUS_CSS_VALUE = /url\s*\(|expression\s*\(|javascript:|(-moz-binding|behavior)\s*:/i

function sanitizeCssValue (style: string): string {
  return style
    .split(';')
    .map(decl => decl.trim())
    .filter((decl) => {
      if (!decl) { return false }
      const colonIndex = decl.indexOf(':')
      if (colonIndex === -1) { return false }

      const property = decl.slice(0, colonIndex).trim().toLowerCase()
      const value = decl.slice(colonIndex + 1).trim().toLowerCase()

      if (!SAFE_CSS_PROPERTIES.has(property)) { return false }
      if (DANGEROUS_CSS_VALUE.test(value)) { return false }
      if (property === 'position' && (value === 'fixed' || value === 'absolute')) { return false }

      return true
    })
    .join('; ')
}

export const useSanitizeHtml = (): { sanitize: (dirtyHtml: string, options?: Config) => string } => {
  const sanitize = (dirtyHtml: string, options?: Config): string => {
    const defaultConfig: Config = {
      ALLOWED_TAGS: [
        'p', 'strong', 'em', 'ul', 'ol', 'li', 'a', 'br',
        'h1', 'h2', 'h3', 'h4', 'h5', 'h6',
        'div', 'span', 'table', 'thead', 'tbody', 'tr', 'th', 'td',
        'b', 'i', 'u', 's', 'small', 'hr', 'pre', 'code', 'blockquote'
      ],
      ALLOWED_ATTR: ['href', 'target', 'rel', 'class', 'colspan', 'rowspan', 'align'],
      ALLOW_DATA_ATTR: false,
      RETURN_DOM: false,
      RETURN_DOM_FRAGMENT: false,
      RETURN_TRUSTED_TYPE: false
    }

    const mergedConfig = { ...defaultConfig, ...options }
    const styleIsAllowed = Array.isArray(mergedConfig.ALLOWED_ATTR) &&
      mergedConfig.ALLOWED_ATTR.includes('style')

    if (styleIsAllowed) {
      DOMPurify.addHook('afterSanitizeAttributes', (node) => {
        if (node.hasAttribute('style')) {
          const sanitized = sanitizeCssValue(node.getAttribute('style') ?? '')
          if (sanitized) {
            node.setAttribute('style', sanitized)
          } else {
            node.removeAttribute('style')
          }
        }
      })
    }

    try {
      return DOMPurify.sanitize(dirtyHtml, mergedConfig)
    } finally {
      if (styleIsAllowed) {
        DOMPurify.removeHook('afterSanitizeAttributes')
      }
    }
  }

  return { sanitize }
}
